import React, { memo } from 'react';
import { Info } from 'lucide-react';
import { cn } from '../../../../../lib/utils';
import { AssetGroup } from '../../../../../types/assetGroup';
import { EquipmentListProps } from '../../../../../types/baseTypes';
import { getEquipmentName } from "../../../../../util";

interface StorageListAccordionProps {
    assetGroup: AssetGroup | EquipmentListProps;
    active: boolean;
    onToggle: (id: string) => void;
}

/**
 * StorageListAccordion
 * ---------------------------------------------------------------
 * Accordion component for displaying and selecting storage/locker sizes.
 * 
 * @remarks
 * - Expandable/collapsible button to show storage details
 * - Displays storage name and description with localization support
 * - Smooth animations for expand/collapse transitions
 * - Accessible with proper ARIA attributes and keyboard navigation
 * - Visual feedback for active/selected state
 */
function StorageListAccordion({ assetGroup, active, onToggle }: StorageListAccordionProps) {
    /**
     * The product image, when the tenant uploaded one.
     *
     * `GET /location/{id}/groups/include-price` already returns `featuredImage`
     * (`groupAppHandler.getAllWithPriceByLocationId` maps `group.featured_image`
     * explicitly, and `getLocationEquipment` has no `transformResponse`, so the
     * raw field reaches the store) — the rental flow renders it from the very
     * same payload. Only this accordion never showed it.
     *
     * Read defensively because the prop is typed `AssetGroup | EquipmentListProps`
     * and only the second declares the field; an absent image renders no slot at
     * all rather than a broken one.
     */
    const featuredImage = (assetGroup as { featuredImage?: string | null }).featuredImage;
    const title = getEquipmentName(assetGroup.name, assetGroup?.langNames, "title");
    // Only the image needs the extra room; a panel without one keeps the height
    // it has always had.
    const openPanelHeight = featuredImage ? "max-h-48" : "max-h-40";

    const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        onToggle(assetGroup.id);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onToggle(assetGroup.id);
        }
    };

    return (
        <div className="mb-2" role="region" aria-label={title}>
            <button
                type="button"
                className={cn(
                    "flex w-full border border-primary py-2 px-4 rounded-xl justify-center text-center items-center cursor-pointer transition-all duration-200 ease-in-out",
                    "hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
                    active ? "bg-primary text-white" : "bg-white text-slate-900 hover:bg-slate-50"
                )}
                onClick={handleToggle}
                onKeyDown={handleKeyDown}
                aria-expanded={active}
                aria-controls={`accordion-content-${assetGroup.id}`}
            >
                <span className="font-bold">{title}</span>
            </button>
            <div
                id={`accordion-content-${assetGroup.id}`}
                className={cn(
                    "overflow-hidden transition-all duration-300 ease-in-out",
                    active ? cn("opacity-100 mt-2", openPanelHeight) : "max-h-0 opacity-0"
                )}
                aria-hidden={!active}
            >
                <div className={cn(
                    "flex items-start animate-in animate-out fade-in slide-in-from-top-2 duration-300",
                    featuredImage ? "gap-3" : "gap-2"
                )}>
                    {featuredImage ? (
                        // Same 64px square, same `object-cover`, as the rental
                        // accordion — one framing across the flows, and the one
                        // the product form now previews.
                        <img
                            src={featuredImage}
                            alt={title}
                            className="h-16 w-16 flex-shrink-0 rounded-md object-cover"
                        />
                    ) : (
                        <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" aria-hidden="true" />
                    )}
                    <p className="text-sm font-semibold text-slate-700 leading-relaxed">
                        {getEquipmentName(assetGroup.description, assetGroup?.langNames, "description")}
                    </p>
                </div>
            </div>
        </div>
    );
}

export default memo(StorageListAccordion);

